import { connect, mapProps } from "@formily/react";
import { Select as AntSelect } from "@formily/antd-v5";
import { useQuery } from "@tanstack/react-query";
import type { TranslatableEnum } from "@rumble-it/common-lib/types/models/TranslatableEnum.interface.ts";

type SelectProps = {
  queryFn?: (params?: Record<string, any>) => Promise<TranslatableEnum[]>;
  queryFnFilter?: string[];
  queryFnSort?: boolean;
  queryFnParams?: Record<string, any>;
  queryFnMap?: (data: any) => TranslatableEnum[];
  children?: React.ReactNode;
  options?: TranslatableEnum[];
  loading?: boolean;
};

const SelectBase = (props: SelectProps) => {
  const enhancedProps = {
    ...props,
    virtual: false, // Disable virtual scrolling by default
  };

  if (props.queryFn) {
    const query = useQuery({
      queryKey: ["selectEnum", props.queryFnFilter, props.queryFnParams],
      queryFn: () => {
        if (!props.queryFn) {
          throw new Error("queryFn is not available");
        }
        if (props.queryFnParams) {
          return props.queryFn(props.queryFnParams);
        } else {
          return props.queryFn();
        }
      },
      select(data) {
        if (props.queryFnMap) {
          return props.queryFnMap(data);
        }
        return data;
      },
    });

    if (props.queryFnFilter && props.queryFnFilter.length > 0 && query.data) {
      // Filter and sort by queryFnFilter order
      query.data = props.queryFnFilter
        .map((filterValue) =>
          query.data!.find((item) => item.value === filterValue),
        )
        .filter(Boolean) as TranslatableEnum[];
    }

    if (props.queryFnSort && query.data) {
      query.data = [...query.data].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    }

    enhancedProps.options = query.data || [];
    enhancedProps.loading = query.isLoading;

    delete enhancedProps.queryFn;
    delete enhancedProps.queryFnFilter;
  }

  return <AntSelect {...enhancedProps}>{enhancedProps.children}</AntSelect>;
};

export const Select = connect(
  SelectBase,
  mapProps((props) => ({
    ...props,
    queryFn: props?.queryFn,
    queryFnFilter: props?.queryFnFilter || [],
    queryFnParams: props?.queryFnParams || {},
    queryFnMap: props?.queryFnMap,
  })),
);

export default Select;
